import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import ar from "./locales/ar.json";
import bg from "./locales/bg.json";
import cs from "./locales/cs.json";
import da from "./locales/da.json";
import de from "./locales/de.json";
import el from "./locales/el.json";
import en from "./locales/en.json";
import es from "./locales/es.json";
import et from "./locales/et.json";
import fi from "./locales/fi.json";
import fr from "./locales/fr.json";
import ga from "./locales/ga.json";
import hr from "./locales/hr.json";
import hu from "./locales/hu.json";
import it from "./locales/it.json";
import lt from "./locales/lt.json";
import lv from "./locales/lv.json";
import mt from "./locales/mt.json";
import nl from "./locales/nl.json";
import pl from "./locales/pl.json";
import pt from "./locales/pt.json";
import ro from "./locales/ro.json";
import ru from "./locales/ru.json";
import sk from "./locales/sk.json";
import sl from "./locales/sl.json";
import sv from "./locales/sv.json";
import uk from "./locales/uk.json";
import zh from "./locales/zh.json";

export const LANGUAGE_STORAGE_KEY = "eudr.language";

/** Official EU languages (ISO 639-1), order: English first then alpha. */
export const EU_OFFICIAL_LANGUAGES = [
  "en",
  "bg",
  "cs",
  "da",
  "de",
  "el",
  "es",
  "et",
  "fi",
  "fr",
  "ga",
  "hr",
  "hu",
  "it",
  "lt",
  "lv",
  "mt",
  "nl",
  "pl",
  "pt",
  "ro",
  "sk",
  "sl",
  "sv",
] as const;

/**
 * Extra product languages (not EU official).
 * `zh` = Simplified Chinese (maps to Ant Design zh_CN / Google zh-CN; not zh-TW).
 */
export const EXTRA_LANGUAGES = ["ru", "uk", "ar", "zh"] as const;

export const APP_LANGUAGES = [...EU_OFFICIAL_LANGUAGES, ...EXTRA_LANGUAGES] as const;

export type AppLanguage = (typeof APP_LANGUAGES)[number];

/** Languages that require RTL layout (html[dir] + Ant Design direction). */
export const RTL_LANGUAGES = new Set<AppLanguage>(["ar"]);

export const LANGUAGE_NATIVE_LABELS: Record<AppLanguage, string> = {
  ar: "العربية",
  bg: "Български",
  cs: "Čeština",
  da: "Dansk",
  de: "Deutsch",
  el: "Ελληνικά",
  en: "English",
  es: "Español",
  et: "Eesti",
  fi: "Suomi",
  fr: "Français",
  ga: "Gaeilge",
  hr: "Hrvatski",
  hu: "Magyar",
  it: "Italiano",
  lt: "Lietuvių",
  lv: "Latviešu",
  mt: "Malti",
  nl: "Nederlands",
  pl: "Polski",
  pt: "Português",
  ro: "Română",
  ru: "Русский",
  sk: "Slovenčina",
  sl: "Slovenščina",
  sv: "Svenska",
  uk: "Українська",
  zh: "简体中文",
};

const SUPPORTED = new Set<string>(APP_LANGUAGES);

const resources = {
  ar: { translation: ar },
  bg: { translation: bg },
  cs: { translation: cs },
  da: { translation: da },
  de: { translation: de },
  el: { translation: el },
  en: { translation: en },
  es: { translation: es },
  et: { translation: et },
  fi: { translation: fi },
  fr: { translation: fr },
  ga: { translation: ga },
  hr: { translation: hr },
  hu: { translation: hu },
  it: { translation: it },
  lt: { translation: lt },
  lv: { translation: lv },
  mt: { translation: mt },
  nl: { translation: nl },
  pl: { translation: pl },
  pt: { translation: pt },
  ro: { translation: ro },
  ru: { translation: ru },
  sk: { translation: sk },
  sl: { translation: sl },
  sv: { translation: sv },
  uk: { translation: uk },
  zh: { translation: zh },
};

export function isAppLanguage(value: string | null | undefined): value is AppLanguage {
  return !!value && SUPPORTED.has(value);
}

export function resolveAppLanguage(lng: string | undefined | null): AppLanguage {
  const raw = (lng || "en").toLowerCase();
  // Prefer exact match for codes like future zh-CN aliases; then primary subtag.
  if (isAppLanguage(raw)) return raw;
  const code = raw.split("-")[0];
  // Map common Chinese region tags onto our Simplified Chinese pack.
  if (code === "zh") return "zh";
  return isAppLanguage(code) ? code : "en";
}

export function isRtlLanguage(lng: string | undefined | null): boolean {
  return RTL_LANGUAGES.has(resolveAppLanguage(lng));
}

function applyDocumentLanguage(resolved: AppLanguage) {
  document.documentElement.lang = resolved === "zh" ? "zh-CN" : resolved;
  document.documentElement.dir = isRtlLanguage(resolved) ? "rtl" : "ltr";
}

function detectLanguage(): AppLanguage {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (isAppLanguage(stored)) {
      return stored;
    }
  } catch {
    /* ignore */
  }
  // English is the product default when the visitor has no saved preference.
  return "en";
}

i18n.use(initReactI18next).init({
  resources,
  lng: detectLanguage(),
  fallbackLng: "en",
  interpolation: { escapeValue: false },
});

i18n.on("languageChanged", (lng) => {
  const resolved = resolveAppLanguage(lng);
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, resolved);
  } catch {
    /* ignore */
  }
  applyDocumentLanguage(resolved);
});

applyDocumentLanguage(resolveAppLanguage(i18n.language));

export default i18n;
